import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { UiAuthService } from '@smatchy/ui-auth';
import { AUTH_RECOVERY_RETRIED, SKIP_AUTH_RECOVERY } from './auth-recovery.context';
import { BackofficeSessionService } from './backoffice-session.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(private readonly authService: UiAuthService, private readonly sessionService: BackofficeSessionService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.shouldHandle(request.url) === false) {
      return next.handle(request);
    }

    if (this.isAuthRoute(request.url)) {
      return next.handle(this.withOptionalToken(request));
    }

    if (this.sessionService.hasPendingSessionWork()) {
      return from(this.sessionService.waitForPendingSessionWork()).pipe(
        switchMap(() => this.handleWithToken(request, next)),
      );
    }

    return this.handleWithToken(request, next);
  }

  private handleWithToken(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.authService.getAccessToken();
    if (token) {
      request = this.withToken(request, token);
    } else {
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Authentication token is missing' }));
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!this.shouldRecover(request, error)) {
          return throwError(() => error);
        }
        return from(this.sessionService.refreshSession()).pipe(
          switchMap((refreshResponse) => {
            if (!refreshResponse.user.roles?.includes('admin')) {
              this.sessionService.endSession();
              return throwError(() => error);
            }
            return next.handle(
              request.clone({
                context: request.context.set(AUTH_RECOVERY_RETRIED, true),
                setHeaders: {
                  Authorization: `Bearer ${refreshResponse.access_token}`,
                },
              }),
            );
          }),
          catchError((refreshError: unknown) => {
            if (this.sessionService.isHardSessionFailure(refreshError)) {
              this.sessionService.endSession();
            }
            return throwError(() => error);
          }),
        );
      }),
    );
  }

  private shouldHandle(url: string) {
    const baseUrl = environment.api;
    if (!baseUrl) {
      return false;
    }

    // if request is not for api, do not intercept
    return url.startsWith(baseUrl) === true;
  }

  private withOptionalToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.authService.getAccessToken();
    return token ? this.withToken(request, token) : request;
  }

  private withToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldRecover(request: HttpRequest<unknown>, error: unknown): boolean {
    if (request.context.get(SKIP_AUTH_RECOVERY) || request.context.get(AUTH_RECOVERY_RETRIED)) {
      return false;
    }
    if (this.isAuthRoute(request.url)) {
      return false;
    }
    return this.sessionService.isAuthTokenInvalid(error);
  }

  private isAuthRoute(url: string): boolean {
    return (
      url.includes('/auth/login') ||
      url.includes('/auth/sign-up') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/session')
    );
  }
}
